import React, { useState } from 'react';
import {
  FormField,
} from '../../../shared/components/form';
import { CALL_PRICING_MODEL_FLAT_RATE } from '../../../shared/constants/common';

const FLAT_RATE_MIN_DURATION = CALL_PRICING_MODEL_FLAT_RATE.MIN_DURATION_MINUTES;
const FLAT_RATE_MAX_DURATION = CALL_PRICING_MODEL_FLAT_RATE.MAX_DURATION_MINUTES;

const CallSettingsSection = ({
  control,
  isEditProfileUpdate
}) => {
  const [voiceDuration, setVoiceDuration] = useState(false);
  const [videoDuration, setVideoDuration] = useState(false);
  const [voiceMinFocused, setVoiceMinFocused] = useState(false);
  const [videoMinFocused, setVideoMinFocused] = useState(false);
  const [callingExplainedOpen, setCallingExplainedOpen] = useState(false);

  const getMinCallMinutesDisplayValue = (storedValue, hasUserEdited, isFocused) => {
    if (storedValue === 1 && !isFocused) return '';
    if ((isEditProfileUpdate || hasUserEdited) && storedValue) return storedValue;
    return '';
  };
  
  return (
    <>
      {/* Voice Call Section - Common for both roles */}
      <div className="unified-edit-section">
        <p className="unified-edit-section-description">
          Charge per-minute or a flat rate for your calls. You can set a required call time for per-minute calls or offer bulk pricing for flat-rate sessions (e.g. $100 for 30 min).
        </p>
        <div className="unified-edit-section-header">
          <i className="fa-solid fa-phone unified-edit-section-icon"></i>
          <span className="unified-edit-section-title">Voice Call</span>
        </div>
        <div className="unified-edit-options">
          <FormField
            control={control}
            name="formData.voice.pricingModel"
            render={({ field }) => (
              <>
                <div className="unified-edit-option">
                  <input
                    type="radio"
                    id="voice-per-min"
                    name="voice-rate"
                    className="unified-edit-radio"
                    checked={field.value === 'per_minute'}
                    onChange={() => field.onChange('per_minute')}
                  />
                  <label htmlFor="voice-per-min" className="unified-edit-option-label">Per Minute</label>
                  <FormField
                    control={control}
                    name="formData.voice.perMinute.amount"
                    render={({ field: amountField, fieldState }) => (
                      <div className={`unified-edit-call-input-wrapper${fieldState.error ? ' unified-edit-call-input-wrapper--error' : ''}`}>
                        <span className="unified-edit-call-prefix">$</span>
                        <input
                          type="text"
                          className="unified-edit-call-input"
                          value={amountField.value || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Only allow numbers and one decimal point
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                              amountField.onChange(value === '' ? '' : parseFloat(value) || value);
                            }
                          }}
                          onBlur={amountField.onBlur}
                          name={amountField.name}
                        />
                      </div>
                    )}
                  />
                  <FormField
                    control={control}
                    name="formData.voice.perMinute.minCallMinutess"
                    render={({ field: durationField }) => (
                      <input
                        type="text"
                        placeholder="Call time req"
                        className="unified-edit-call-duration"
                        value={getMinCallMinutesDisplayValue(
                          durationField.value,
                          voiceDuration,
                          voiceMinFocused
                        )}
                        onFocus={() => setVoiceMinFocused(true)}
                        onChange={(e) => {
                          setVoiceDuration(true);
                          const value = e.target.value;
                          // Only allow integers (no decimal point)
                          if (value === '' || /^\d+$/.test(value)) {
                            const numValue = value === '' ? '' : parseInt(value) || '';
                            durationField.onChange(numValue);
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          const numValue = value === '' ? '' : parseInt(value) || '';
                          if (numValue !== undefined && numValue < 0) {
                            durationField.onChange(1);
                            setVoiceDuration(false);
                          } else if (numValue === '' || numValue === 1) {
                            durationField.onChange(1);
                            setVoiceDuration(false);
                          } else {
                            setVoiceDuration(true);
                          }
                          setVoiceMinFocused(false);
                          durationField.onBlur();
                        }}
                        name={durationField.name}
                      />
                    )}
                  />
                </div>
                <div className="unified-edit-option">
                  <input
                    type="radio"
                    id="voice-flat"
                    name="voice-rate"
                    className="unified-edit-radio"
                    checked={field.value === 'flat_rate'}
                    onChange={() => field.onChange('flat_rate')}
                  />
                  <label htmlFor="voice-flat" className="unified-edit-option-label">Flat Rate</label>
                  <FormField
                    control={control}
                    name="formData.voice.flatRate.amount"
                    render={({ field: amountField, fieldState }) => (
                      <div className={`unified-edit-call-input-wrapper${fieldState.error ? ' unified-edit-call-input-wrapper--error' : ''}`}>
                        <span className="unified-edit-call-prefix">$</span>
                        <input
                          type="text"
                          className="unified-edit-call-input"
                          value={amountField.value || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Only allow numbers and one decimal point
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                              amountField.onChange(value === '' ? '' : parseFloat(value) || value);
                            }
                          }}
                          onBlur={amountField.onBlur}
                          name={amountField.name}
                        />
                      </div>
                    )}
                  />
                  <FormField
                    control={control}
                    name="formData.voice.flatRate.durationMinutes"
                    render={({ field: durationField }) => (
                      <input
                        type="text"
                        placeholder="Duration"
                        className="unified-edit-call-duration"
                        value={durationField.value || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow integers (no decimal point)
                          if (value === '' || /^\d+$/.test(value)) {
                            const numValue = value === '' ? '' : parseInt(value) || '';
                            durationField.onChange(numValue);
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          const numValue = value === '' ? '' : parseInt(value) || '';
                          if (numValue !== undefined) {
                            if (numValue < FLAT_RATE_MIN_DURATION) {
                              durationField.onChange(FLAT_RATE_MIN_DURATION);
                            } else if (numValue > FLAT_RATE_MAX_DURATION) {
                              durationField.onChange(FLAT_RATE_MAX_DURATION);
                            }
                          } else if (numValue === '') {
                            durationField.onChange(FLAT_RATE_MIN_DURATION);
                          }
                          durationField.onBlur();
                        }}
                        name={durationField.name}
                      />
                    )}
                  />
                </div>
              </>
            )}
          />
        </div>
      </div>

      {/* Video Call Section - Common for both roles */}
      <div className="unified-edit-section">
        <div className="unified-edit-section-header">
          <i className="fa-solid fa-video unified-edit-section-icon"></i>
          <span className="unified-edit-section-title">Video Call</span>
        </div>
        <div className="unified-edit-options">
          <FormField
            control={control}
            name="formData.video.pricingModel"
            render={({ field }) => (
              <>
                <div className="unified-edit-option">
                  <input
                    type="radio"
                    id="video-per-min"
                    name="video-rate"
                    className="unified-edit-radio"
                    checked={field.value === 'per_minute'}
                    onChange={() => field.onChange('per_minute')}
                  />
                  <label htmlFor="video-per-min" className="unified-edit-option-label">Per Minute</label>
                  <FormField
                    control={control}
                    name="formData.video.perMinute.amount"
                    render={({ field: amountField, fieldState }) => (
                      <div className={`unified-edit-call-input-wrapper${fieldState.error ? ' unified-edit-call-input-wrapper--error' : ''}`}>
                        <span className="unified-edit-call-prefix">$</span>
                        <input
                          type="text"
                          className="unified-edit-call-input"
                          value={amountField.value || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Only allow numbers and one decimal point
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                              amountField.onChange(value === '' ? '' : parseFloat(value) || value);
                            }
                          }}
                          onBlur={amountField.onBlur}
                          name={amountField.name}
                        />
                      </div>
                    )}
                  />
                  <FormField
                    control={control}
                    name="formData.video.perMinute.minCallMinutess"
                    render={({ field: durationField }) => (
                      <input
                        type="text"
                        placeholder="Call time req"
                        className="unified-edit-call-duration"
                        value={getMinCallMinutesDisplayValue(
                          durationField.value,
                          videoDuration,
                          videoMinFocused
                        )}
                        onFocus={() => setVideoMinFocused(true)}
                        onChange={(e) => {
                          setVideoDuration(true);
                          const value = e.target.value;
                          // Only allow integers (no decimal point)
                          if (value === '' || /^\d+$/.test(value)) {
                            const numValue = value === '' ? '' : parseInt(value) || '';
                            durationField.onChange(numValue);
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          const numValue = value === '' ? '' : parseInt(value) || '';
                          if (numValue !== undefined && numValue < 0) {
                            durationField.onChange(1);
                            setVideoDuration(false);
                          } else if (numValue === '' || numValue === 1) {
                            durationField.onChange(1);
                            setVideoDuration(false);
                          } else {
                            setVideoDuration(true);
                          }
                          setVideoMinFocused(false);
                          durationField.onBlur();
                        }}
                        name={durationField.name}
                      />
                    )}
                  />
                </div>
                <div className="unified-edit-option">
                  <input
                    type="radio"
                    id="video-flat"
                    name="video-rate"
                    className="unified-edit-radio"
                    checked={field.value === 'flat_rate'}
                    onChange={() => field.onChange('flat_rate')}
                  />
                  <label htmlFor="video-flat" className="unified-edit-option-label">Flat Rate</label>
                  <FormField
                    control={control}
                    name="formData.video.flatRate.amount"
                    render={({ field: amountField, fieldState }) => (
                      <div className={`unified-edit-call-input-wrapper${fieldState.error ? ' unified-edit-call-input-wrapper--error' : ''}`}>
                        <span className="unified-edit-call-prefix">$</span>
                        <input
                          type="text"
                          className="unified-edit-call-input"
                          value={amountField.value || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Only allow numbers and one decimal point
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                              amountField.onChange(value === '' ? '' : parseFloat(value) || value);
                            }
                          }}
                          onBlur={amountField.onBlur}
                          name={amountField.name}
                        />
                      </div>
                    )}
                  />
                  <FormField
                    control={control}
                    name="formData.video.flatRate.durationMinutes"
                    render={({ field: durationField }) => (
                      <input
                        type="text"
                        placeholder="Duration"
                        className="unified-edit-call-duration"
                        value={durationField.value || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow integers (no decimal point)
                          if (value === '' || /^\d+$/.test(value)) {
                            const numValue = value === '' ? '' : parseInt(value) || '';
                            durationField.onChange(numValue);
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          const numValue = value === '' ? '' : parseInt(value) || '';
                          if (numValue !== undefined) {
                            if (numValue < FLAT_RATE_MIN_DURATION) {
                              durationField.onChange(FLAT_RATE_MIN_DURATION);
                            } else if (numValue > FLAT_RATE_MAX_DURATION) {
                              durationField.onChange(FLAT_RATE_MAX_DURATION);
                            }
                          } else if (numValue === '') {
                            durationField.onChange(FLAT_RATE_MIN_DURATION);
                          }
                          durationField.onBlur();
                        }}
                        name={durationField.name}
                      />
                    )}
                  />
                </div>
              </>
            )}
          />
        </div>
      </div>

      <div className="unified-edit-warning-banner unified-edit-calling-explained">
        <button
          type="button"
          className="unified-edit-calling-explained-toggle"
          onClick={() => setCallingExplainedOpen((prev) => !prev)}
          aria-expanded={callingExplainedOpen}
        >
          <i className="fa-solid fa-info-circle unified-edit-warning-icon"></i>
          <span className="unified-edit-calling-explained-title">Calling Explained</span>
          <i
            className={`fa-solid fa-chevron-${callingExplainedOpen ? 'up' : 'down'} unified-edit-calling-explained-chevron`}
            aria-hidden
          ></i>
        </button>
        {callingExplainedOpen && (
          <div className="unified-edit-calling-explained-content">
            <p>
              Per-minute sessions bill by call duration. If a call time requirement is set, you will receive the full required amount if the subscriber ends the call early. Example: $10/min with a 5-minute requirement pays $50 even if the call ends after 1 minute. If you end the call early, you will only be paid for the time completed at your per minute rate.
            </p>
            <p>
              Flat-rate sessions charge upfront and recharge each time a new time block is reached. Example: $100 per 15 minutes charges the subscriber $100 every 15 minutes on the call.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default CallSettingsSection;

